import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  makeStyles,
  IconButton,
} from "@material-ui/core";
import { ExpandMore as ExpandMoreIcon, Close as CloseIcon } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    background: "linear-gradient(to right, #182229, #202d37)",
    color: "#ffffff",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 24px",
  },
  dialogTitleText: {
    color: "#ffffff",
    fontWeight: 700,
    fontSize: "1.15rem",
  },
  dialogContent: {
    padding: "16px 24px",
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
  },
  dialogActions: {
    padding: "12px 24px",
    borderTop: "1px solid rgba(0, 0, 0, 0.1)",
    backgroundColor: theme.palette.background.default,
  },
  closeBtn: {
    color: "#ffffff",
    padding: 4,
  },
  accordion: {
    marginBottom: 8,
    borderRadius: 8,
    overflow: "hidden",
    "&::before": { display: "none" },
    border: "1px solid rgba(0, 0, 0, 0.1)",
  },
  accordionSummary: {
    backgroundColor: theme.mode === "light" ? "rgba(0, 0, 0, 0.03)" : "rgba(255, 255, 255, 0.05)",
    minHeight: 56,
    "&$expanded": { minHeight: 56 },
  },
  expanded: {},
  accIcon: {
    color: theme.palette.primary.main,
    fontSize: 22,
    marginRight: 10,
  },
  accTitle: {
    fontWeight: 600,
    fontSize: "0.98rem",
    display: "flex",
    alignItems: "center",
  },
  accDetails: {
    padding: "12px 20px 16px",
    display: "block",
  },
  paragraph: {
    marginBottom: 10,
    fontSize: "0.9rem",
    lineHeight: 1.6,
  },
  introBox: {
    backgroundColor: theme.mode === "light" ? "rgba(0, 122, 255, 0.08)" : "rgba(0, 122, 255, 0.12)",
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    borderRadius: 6,
    padding: "12px 16px",
    marginBottom: 16,
  },
  list: {
    paddingLeft: "22px",
    marginBottom: 12,
    "& li": {
      fontSize: "0.9rem",
      lineHeight: 1.55,
      marginBottom: 6,
    },
  },
  ruleBox: {
    backgroundColor: theme.mode === "light" ? "rgba(255, 193, 7, 0.12)" : "rgba(255, 193, 7, 0.08)",
    borderLeft: "4px solid #ffc107",
    borderRadius: 6,
    padding: "10px 14px",
    marginTop: 8,
    marginBottom: 8,
    "& .ruleTitle": {
      fontWeight: 700,
      fontSize: "0.85rem",
      color: "#b8860b",
      marginBottom: 4,
    },
    "& p": {
      fontSize: "0.85rem",
      margin: 0,
      lineHeight: 1.5,
    },
  },
  sectionLabel: {
    fontWeight: 700,
    fontSize: "0.85rem",
    color: theme.palette.primary.main,
    marginTop: 10,
    marginBottom: 4,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
}));

const Regra = ({ titulo, children }) => {
  const classes = useStyles();
  return (
    <Box className={classes.ruleBox}>
      <div className="ruleTitle">⚠️ Atenção — {titulo}</div>
      <p>{children}</p>
    </Box>
  );
};

const TutorialAtendimentos = ({ open, onClose }) => {
  const classes = useStyles();
  const [expandido, setExpandido] = useState("painel-busca");

  const handleChange = (panel) => (event, isExpanded) => {
    setExpandido(isExpanded ? panel : false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      aria-labelledby="tutorial-atendimentos-title"
    >
      <DialogTitle id="tutorial-atendimentos-title" className={classes.dialogTitle} disableTypography>
        <Typography component="h2" className={classes.dialogTitleText}>
          🎓 Tutorial Detalhado — Tela de Atendimentos
        </Typography>
        <IconButton size="small" className={classes.closeBtn} onClick={onClose} aria-label="Fechar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className={classes.dialogContent}>
        <Box className={classes.introBox}>
          <Typography className={classes.paragraph} style={{ marginBottom: 0 }}>
            Este tutorial explica <strong>detalhadamente</strong> como usar cada parte da tela de
            Atendimentos. Clique em cada seção abaixo para abrir e ver as explicações.
          </Typography>
        </Box>

        {/* 1. VISÃO GERAL */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "visao-geral"}
          onChange={handleChange("visao-geral")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>🏠</span> Visão Geral da Tela
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              Esta é a <strong>tela de trabalho do dia a dia</strong> do atendente. É aqui que você
              conversa com os clientes pelo WhatsApp da empresa. A tela é dividida em duas partes:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Lado esquerdo:</strong> a lista de conversas, separada em abas (Atendendo, Aguardando, Grupos). No topo ficam a busca, os filtros e os botões de ações rápidas.</li>
              <li><strong>Lado central (o chat):</strong> a conversa do cliente selecionado, com a caixa de mensagem embaixo e os botões de ação em cima.</li>
            </ul>
            <Typography className={classes.paragraph}>
              Cada aba mostra um <strong>numerinho (contador)</strong> que mostra quantas conversas
              existem ali. Esse número se atualiza sozinho, na hora, assim que chega uma mensagem nova
              ou muda alguma coisa — não precisa recarregar a página.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* 2. BARRA DE BUSCA E FILTROS */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "painel-busca"}
          onChange={handleChange("painel-busca")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>🔍</span> Barra de Busca e Filtros
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <div className={classes.sectionLabel}>Como usar</div>
            <ul className={classes.list}>
              <li><strong>Caixa de pesquisa ("Buscar atendimento e mensagens"):</strong> digite o nome ou o telefone do cliente para achá-lo na lista que está aberta.</li>
              <li><strong>Chave "Pesquisar nas mensagens":</strong> quando ligada, a busca também procura dentro das <em>mensagens</em> já enviadas (ex: procurar "comprovante"). É mais demorada porque vasculha toda a conversa. Ao buscar, a tela troca para a aba <strong>Pesquisa</strong>.</li>
              <li><strong>Botão de Filtros (ícone de funil):</strong> abre um painel com vários filtros que podem ser combinados.</li>
            </ul>

            <div className={classes.sectionLabel}>Filtros disponíveis no painel</div>
            <ul className={classes.list}>
              <li><strong>Etiquetas (Tags):</strong> filtra os clientes por categorias coloridas (ex: "Boleto Emitido").</li>
              <li><strong>Conexões:</strong> mostra só os chamados de um número de WhatsApp específico.</li>
              <li><strong>Status:</strong> filtra por chamados abertos, fechados ou aguardando.</li>
              <li><strong>Atendentes (somente para administradores):</strong> mostra os chats de outro atendente.</li>
              <li><strong>Empresa:</strong> filtra pelo nome da empresa cadastrada no contato.</li>
              <li><strong>Data Inicial / Data Final:</strong> filtra por período (bem útil na aba Resolvidos e na busca).</li>
            </ul>

            <Regra titulo="Aba de Pesquisa">
              Ao digitar algo na busca, o sistema troca automaticamente para a aba{" "}
              <strong>Pesquisa</strong>. Ela ignora a aba que você estava e mostra resultados de
              qualquer situação (abertos, fechados, aguardando) de acordo com o que você pesquisou.
              Administradores veem resultados de todos os atendentes; atendentes comuns veem apenas os
              seus.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 3. ABA ATENDENDO */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "atendendo"}
          onChange={handleChange("atendendo")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>💬</span> Aba Atendendo
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              Aqui ficam as conversas que estão <strong>sob a sua responsabilidade no momento</strong>.
              Só você responde esses clientes. O numerinho mostra quantos atendimentos você tem em
              mãos agora.
            </Typography>
            <div className={classes.sectionLabel}>Como uma conversa chega aqui</div>
            <ul className={classes.list}>
              <li>Você clica em <strong>Aceitar</strong> num cliente da aba Aguardando.</li>
              <li>Você cria uma conversa pelo botão <strong>Novo Ticket</strong> (ela já nasce assumida por você).</li>
              <li>Um colega ou administrador <strong>transfere</strong> uma conversa para você.</li>
            </ul>

            <Regra titulo="Toda conversa aqui tem dono">
              Uma conversa só fica na aba Atendendo se tiver um atendente responsável por ela. Se por
              algum motivo ela ficar sem dono, o sistema automaticamente a manda de volta para a aba
              Aguardando, para que alguém possa assumir.
            </Regra>

            <Regra titulo="Aceitar desliga o robô">
              Quando você assume uma conversa que estava sendo atendida por um robô (chatbot), o
              sistema <strong>desliga o robô na mesma hora</strong> e passa o atendimento para você,
              de pessoa para pessoa. Se a empresa tiver uma mensagem de saudação configurada, ela é
              enviada automaticamente para o cliente assim que você aceita.
            </Regra>

            <Regra titulo="Só um atendente por cliente">
              Assim que você aceita uma conversa, ela <strong>some da lista "Aguardando" dos seus
              colegas na mesma hora</strong>. Assim ninguém mais vai tentar responder o mesmo cliente
              ao mesmo tempo — a menos que seja um administrador ou tenha a opção de ver tudo ligada.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 4. ABA AGUARDANDO */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "aguardando"}
          onChange={handleChange("aguardando")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>⏳</span> Aba Aguardando
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              Aqui ficam os clientes novos que mandaram mensagem e <strong>estão esperando alguém
              atender</strong>. Ninguém assumiu ainda. O numerinho mostra quantos estão na fila de
              espera.
            </Typography>

            <div className={classes.sectionLabel}>O que você pode fazer aqui</div>
            <ul className={classes.list}>
              <li><strong>Aceitar (sinal verde de "check"):</strong> assume a conversa. Se ela ainda <em>não tiver um setor/fila</em> definido, abre uma janelinha para você escolher o setor antes. Se já tiver setor, assume direto e a conversa vai para a aba Atendendo.</li>
              <li><strong>Ignorar/Fechar (X):</strong> só aparece se o seu cadastro permitir fechar conversas pendentes ou se você for administrador. Fecha a conversa <em>sem</em> mandar mensagem de despedida para o cliente.</li>
            </ul>

            <Regra titulo="Você só vê os clientes dos seus setores">
              Você só vê na aba Aguardando os clientes que caíram nos <strong>setores (filas) dos quais
              você faz parte</strong>. Se você não está em nenhum setor, <strong>não vai receber
              clientes esperando automaticamente</strong>. O seletor de filas lá em cima também serve
              para filtrar essa lista.
            </Regra>

            <Regra titulo="Ver clientes sem setor">
              Se o seu cadastro tiver a permissão especial de ver todos os tickets, você também verá
              os clientes que chegaram sem setor definido. Caso contrário, você vê apenas os dos seus
              setores.
            </Regra>

            <Regra titulo="Mensagens borradas">
              Se o seu cadastro não permitir ler mensagens de conversas pendentes, as mensagens do
              cliente ficam <strong>borradas (embaçadas)</strong> até você aceitar a conversa. Isso
              evita que alguém "espie" a conversa antes de realmente assumir o atendimento.
            </Regra>

            <Regra titulo="Conversas de LGPD para administradores">
              Quando um administrador liga o "Mostrar Todos", a aba Aguardando passa a mostrar também
              as conversas que estão aguardando tratamento de dados (LGPD), além dos clientes
              pendentes normais.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 5. ABA GRUPOS */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "grupos"}
          onChange={handleChange("grupos")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>👥</span> Aba Grupos
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              Aqui ficam as conversas em <strong>grupos de WhatsApp</strong> (várias pessoas juntos).
              Essa aba só aparece se o administrador liberou o atendimento de grupos no seu cadastro.
            </Typography>

            <Regra titulo="Grupos são separados dos atendimentos normais">
              Os grupos ficam numa aba separada e não se misturam com os atendimentos individuais
              (de pessoa para pessoa). Assim você consegue organizar melhor o que é conversa em grupo
              e o que é conversa com um cliente só.
            </Regra>

            <Regra titulo="Só aparece se estiver liberado">
              Se o administrador não liberou o atendimento de grupos no seu cadastro de usuário, a aba
              Grupos <strong>não aparece</strong> para você e as conversas de grupo não aparecem na
              sua listagem.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 6. RESOLVIDOS */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "resolvidos"}
          onChange={handleChange("resolvidos")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>✅</span> Resolvidos / Histórico
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              Aqui ficam todos os atendimentos <strong>já encerrados</strong>. Você acessa clicando
              no botão <strong>"Resolvidos"</strong> (ícone de caixa de seleção) lá em cima, que troca
              a tela para o histórico. Dá para procurar conversas antigas pelo nome, telefone,
              etiquetas e período.
            </Typography>

            <div className={classes.sectionLabel}>O que você pode fazer aqui</div>
            <ul className={classes.list}>
              <li><strong>Reabrir (ícone de seta girando):</strong> reabre a conversa. Se ela não tinha setor, abre uma janela para você escolher o setor antes; se já tinha, reassume direto e a conversa volta para a aba Atendendo.</li>
              <li><strong>Filtro por Data Inicial/Final:</strong> disponível aqui para você limitar o período que quer ver do histórico.</li>
            </ul>

            <Regra titulo="Sem repetir o mesmo cliente">
              A lista de Resolvidos mostra apenas o <strong>último atendimento fechado de cada cliente
              em cada número de WhatsApp</strong>. Assim o mesmo cliente não aparece várias vezes
              quando houve muitos atendimentos encerrados com ele.
            </Regra>

            <Regra titulo="O que cada um vê">
              Administradores e usuários que podem ver tudo, <em>sem</em> o "Mostrar Todos" ligado,
              veem apenas os seus próprios atendimentos encerrados. <em>Com</em> o "Mostrar Todos"
              ligado, veem de todos os setores. Atendentes comuns veem apenas os seus.
            </Regra>

            <Regra titulo="Reabrir sem duplicar">
              Ao reabrir uma conversa, se já existir outra conversa aberta com o mesmo cliente no
              mesmo número, o sistema <strong>reabre aquela conversa que já existe</strong> em vez de
              criar uma nova. Assim o histórico do cliente fica sempre junto, sem repetir.
            </Regra>

            <Regra titulo="Como funciona o encerramento">
              Ao encerrar uma conversa: se o seu cadastro exigir informar valor de venda, vai abrir
              uma telinha para você dizer se houve venda e o valor antes de fechar. Se a empresa
              exigir etiquetas, o contato precisa ter etiquetas cadastradas para conseguir fechar.
              Senão, a conversa é encerrada direto.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 7. SELETOR DE FILAS */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "filas"}
          onChange={handleChange("filas")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>🗂️</span> Seletor de Filas (setores)
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              É a caixinha <strong>"Filas"</strong> lá em cima, do lado dos botões. Ela filtra{" "}
              <strong>todas as abas</strong> (Atendendo, Aguardando, Grupos, Resolvidos e a busca)
              pelos setores que você escolher.
            </Typography>
            <ul className={classes.list}>
              <li><strong>Padrão:</strong> já vem marcada com todos os setores dos quais você faz parte.</li>
              <li><strong>Multi-seleção:</strong> marque ou desmarque para comparar setores ou isolar um só.</li>
              <li>Se você desmarcar todos, a lista fica vazia (nenhuma conversa aparece).</li>
            </ul>

            <Regra titulo="Funciona em todo lugar">
              A escolha das filas afeta todas as listas da tela de atendimentos ao mesmo tempo. Ou
              seja, se você selecionar só o setor "Vendas", vai ver apenas as conversas de Vendas em
              todas as abas.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 8. NOVO TICKET */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "novo-ticket"}
          onChange={handleChange("novo-ticket")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>➕</span> Botão Novo Ticket
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              O botão de <strong>"+"</strong> abre a janela <strong>Novo Ticket</strong>, que deixa
              você começar uma conversa com um cliente que ainda não mandou mensagem — você vai até
              ele.
            </Typography>

            <div className={classes.sectionLabel}>Como preencher</div>
            <ul className={classes.list}>
              <li><strong>Contato:</strong> comece a digitar o nome (busca a partir de 3 letras). Se o cliente não existir, o sistema oferece criar um novo ali mesmo.</li>
              <li><strong>Fila/setor (obrigatório):</strong> se você não escolher, o sistema avisa "Selecione uma fila". Se você tiver só um setor, ele já vem escolhido sozinho.</li>
              <li><strong>Conexão WhatsApp:</strong> o número padrão é o que está no seu cadastro. Você pode trocar se quiser.</li>
            </ul>

            <Regra titulo="Já nasce assumido por você">
              A conversa já é criada <strong>na sua aba Atendendo</strong>, ou seja, já começa sendo
              sua. Ela não vai para a fila de espera.
            </Regra>

            <Regra titulo="Não duplica cliente">
              Se já existir uma conversa aberta com aquele cliente, o sistema avisa e mostra{" "}
              <strong>qual atendente e setor já está cuidando</strong> dele. Você não vai criar uma
              segunda conversa para o mesmo cliente.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 9. MOSTRAR TODOS */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "mostrar-todos"}
          onChange={handleChange("mostrar-todos")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>👁️</span> Botão Mostrar/Ocultar Todos
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              O botão de <strong>olho</strong> deixa você ver as conversas de <strong>todos os
              atendentes</strong> da equipe, não só as suas.
            </Typography>
            <ul className={classes.list}>
              <li>Só aparece para <strong>administradores</strong> ou para quem tem permissão especial para ver todos os atendimentos.</li>
              <li>Quando ligado, as listas passam a mostrar as conversas dos outros atendentes também (em todas as abas).</li>
            </ul>

            <Regra titulo="Sempre começa desligado">
              Ao abrir a tela, o "Mostrar Todos" <strong>começa desligado</strong>, mesmo para
              administradores. Assim você sempre começa vendo apenas os seus atendimentos e só liga a
              visão geral se quiser.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 10. SELEÇÃO MÚLTIPLA */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "selecao-multipla"}
          onChange={handleChange("selecao-multipla")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>☑️</span> Botão Seleção Múltipla
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              O botão de <strong>caixinha de seleção</strong> liga o <strong>modo de selecionar
              várias conversas ao mesmo tempo</strong>.
            </Typography>
            <ul className={classes.list}>
              <li>Aparecem caixinhas em cada conversa da lista.</li>
              <li>Surge uma <strong>barra embaixo da tela</strong> com ações para fechar ou transferir várias conversas de uma vez.</li>
              <li>Clique de novo no botão para sair do modo de seleção.</li>
            </ul>

            <Regra titulo="Ações em grupo">
              <strong>Fechar todos:</strong> encerra todas as conversas selecionadas de uma vez.{" "}
              <strong>Transferir todos:</strong> passa várias conversas para outro atendente de uma
              só vez — e ao transferir para uma pessoa, a conversa já vai direto para a aba Atendendo
              dela (a pessoa já assume o atendimento).
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 11. ORDENAÇÃO */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "ordenacao"}
          onChange={handleChange("ordenacao")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>↕️</span> Botão Ordenação (Crescente/Decrescente)
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              Muda a <strong>ordem</strong> em que as conversas aparecem na lista, pela data da
              última atualização.
            </Typography>
            <ul className={classes.list}>
              <li><strong>Crescente:</strong> as mais antigas aparecem primeiro (ótimo para não deixar ninguém esperando há muito tempo).</li>
              <li><strong>Decrescente:</strong> as mais recentes aparecem primeiro.</li>
            </ul>

            <Regra titulo="Onde aparece">
              Esse botão só aparece nas abas de atendimentos abertos (Atendendo, Aguardando e Grupos).
              <strong> Não aparece</strong> em Resolvidos nem na busca, pois nesses casos a ordem
              segue os filtros de data que você escolheu.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 12. AÇÕES DO CHAT */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "acoes-chat"}
          onChange={handleChange("acoes-chat")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>🛠️</span> Ações dentro da Conversa (botões em cima do chat)
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Typography className={classes.paragraph}>
              Esses botões aparecem <strong>em cima da conversa</strong> quando você abre um
              atendimento:
            </Typography>
            <ul className={classes.list}>
              <li><strong>Resolver/Encerrar (X):</strong> encerra a conversa. Pode abrir a telinha de valor de venda (se a empresa exigir) ou pedir etiquetas no contato antes de fechar.</li>
              <li><strong>Devolver à Fila (seta de voltar):</strong> devolve o cliente para a fila de espera (Aguardando) para outro atendente assumir.</li>
              <li><strong>Transferir (duas setas):</strong> abre uma janela para escolher para qual colega ou setor a conversa vai. Dá para escrever um <em>lembrete secreto</em> só para a equipe ver.</li>
              <li><strong>Vincular Carteira (carteira):</strong> só aparece se a empresa usa carteira de clientes e o contato ainda não tem. Depois de vincular, novos chamados desse cliente virão direto para você.</li>
              <li><strong>Botão do Robô (chave liga/desliga):</strong> liga ou desliga o robô nesta conversa. Desligar deixa você conversar de pessoa para pessoa; ligar volta o atendimento automático.</li>
              <li><strong>Respostas Rápidas (raio):</strong> abre as mensagens prontas da empresa para você clicar e enviar em segundos.</li>
              <li><strong>Copiar Telefone (duas folhinhas):</strong> copia o número do cliente para a área de transferência.</li>
              <li><strong>Exportar PDF:</strong> baixa toda a conversa em um arquivo PDF.</li>
            </ul>
            <Typography className={classes.paragraph}>
              Para conversas <strong>fechadas</strong>, aparece o botão <strong>Reabrir</strong> no
              lugar dessas ações.
            </Typography>

            <Regra titulo="O lembrete de transferência é secreto">
              O lembrete que você escreve ao transferir é <strong>guardado junto à conversa</strong>{" "}
              e aparece no histórico marcado como mensagem interna da equipe. O cliente{" "}
              <strong>nunca recebe</strong> esse texto no WhatsApp — só quem é da empresa vê.
            </Regra>
          </AccordionDetails>
        </Accordion>

        {/* 13. REGRAS GLOBAIS */}
        <Accordion
          className={classes.accordion}
          expanded={expandido === "regras-globais"}
          onChange={handleChange("regras-globais")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
            classes={{ expanded: classes.expanded }}
          >
            <Typography className={classes.accTitle}>
              <span className={classes.accIcon}>🛡️</span> Regras Gerais do Atendimento
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetails}>
            <Regra titulo="Cada um só vê os seus setores">
              Atendentes só veem as conversas dos setores (filas) dos quais fazem parte. Quem não
              está em nenhum setor não recebe clientes esperando automaticamente.
            </Regra>
            <Regra titulo="Só um atendente por cliente">
              Ao aceitar uma conversa, ela sai da lista dos colegas na mesma hora. Assim duas pessoas
              nunca respondem o mesmo cliente ao mesmo tempo. A exceção é o administrador ou quem tem
              a visão geral ligada.
            </Regra>
            <Regra titulo="Valor de venda ao fechar">
              Se a empresa exigir, a conversa <strong>não é encerrada</strong> enquanto você não
              informar se houve venda e o valor dela na tela de fechamento.
            </Regra>
            <Regra titulo="Pesquisa de satisfação">
              A pesquisa de satisfação é enviada <strong>depois que a conversa é encerrada</strong>,
              se estiver configurada. Se o cliente responder, a conversa fica classificada e
              fechada de vez. Se o cliente mandar uma mensagem nova <em>antes</em> de responder, o
              robô volta a funcionar e a pesquisa anterior é cancelada.
            </Regra>
            <Regra titulo="Tudo em tempo real">
              Todas as listas se atualizam sozinhas, na hora. Quando chega mensagem nova, muda alguma
              conversa ou atualiza um contato, isso aparece <strong>na mesma hora</strong> em todas as
              abas, sem precisar recarregar a página.
            </Regra>
          </AccordionDetails>
        </Accordion>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={onClose}
          color="primary"
          variant="contained"
          style={{ textTransform: "none", fontWeight: 600, borderRadius: 8 }}
        >
          Entendi, fechar tutorial
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TutorialAtendimentos;
